import { intercomTarget } from '@/helpers/intercomTarget';
import { Box, Card, Spinner } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  AccountMilestoneStatus,
  ChangeOrderDocument,
  ChangeOrderStatus,
  INSTALL_APPROVED_MILESTONE_STATUSES,
  MilestoneType,
  PreBillingCustomerDiscountStatus,
  ProgramType,
  Quote,
  QuoteStatus,
  SystemDesignReturnType,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetChangeOrdersQuery } from '../../services/changeOrders';
import { useGetContractsQuery } from '../../services/contract';
import { useGetQuotesQuery } from '../../services/quotes';
import { ContractListData } from '../../types/Contracts';
import {
  CHILD_INDENT,
  ConnectorChildArc,
  ConnectorParentLine,
  INFO_CONNECTOR_COLOR,
  SUCCESS_CONNECTOR_COLOR,
  WARNING_CONNECTOR_COLOR,
} from '../ChangeOrder/changeOrderConnector';

import { ChangeOrderContractRow } from './ChangeOrderContractRow';
import { ContractListItem } from './ContractListItem';

export function Contracts({ className }: { className?: string }) {
  const { id } = useParams<{ id: any }>();
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const programType = account?.programType;
  const isInstallApproved = account?.milestones?.some(
    (milestone: { type: MilestoneType; status: AccountMilestoneStatus }) =>
      milestone.type === MilestoneType.install && INSTALL_APPROVED_MILESTONE_STATUSES.has(milestone.status),
  );
  const { data: contracts, isLoading: isContractLoading } = useGetContractsQuery(id);
  const { data: quotes, isLoading: quotesIsLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType },
  );
  const isLoading = isAccountLoading || quotesIsLoading || isContractLoading;

  const activeQuote = useMemo(() => {
    return quotes?.find((quote) => quote.status === 'active');
  }, [quotes]);

  const { easyChangeOrder = false } = useFlags();
  const hasPendingChangeOrder = quotes?.some((q) => q.status === QuoteStatus.changeOrderPending);
  const canCreateChangeOrder = easyChangeOrder && !hasPendingChangeOrder;
  const { data: changeOrders } = useGetChangeOrdersQuery(id, { skip: !id || !easyChangeOrder });

  const contractsQuotes = useMemo(() => {
    if (isLoading) return [];
    if (!contracts) return [];
    const contractData: Array<ContractListData> = [];
    for (const contract of contracts) {
      const quote = quotes?.find((q) => q.id === contract.quoteId);
      contractData.push({ quote, contract });
    }
    return contractData;
  }, [contracts, quotes, isLoading]);

  return (
    <>
      <Card className={className}>
        <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
          <Box
            as="h3"
            fontWeight="medium"
            fontSize="md"
            data-intercom-target={intercomTarget('account', 'contracts')}
          >
            Contracts
          </Box>
        </Box>
        <ContractsList
          isLoading={isLoading}
          items={contractsQuotes}
          programType={programType}
          activeQuote={activeQuote}
          systemDesign={account?.systemDesign}
          preBillingCustomerDiscount={account?.preBillingCustomerDiscount}
          canCreateChangeOrder={canCreateChangeOrder}
          isInstallApproved={isInstallApproved}
          changeOrders={changeOrders}
          quotes={quotes}
          accountId={id}
        />
      </Card>
    </>
  );
}

// Connector color follows the addendum's CO status, per the status legend: yellow = voided/
// cancelled, green = executed, blue = everything else (pending/sent/viewed/etc.).
export function getAddendumConnectorColor(status: ChangeOrderStatus): string {
  if (status === ChangeOrderStatus.voided || status === ChangeOrderStatus.cancelled) return WARNING_CONNECTOR_COLOR;
  if (status === ChangeOrderStatus.executed) return SUCCESS_CONNECTOR_COLOR;
  return INFO_CONNECTOR_COLOR;
}

// Renders contract rows, and beneath any contract with change orders hanging off it, renders EVERY
// addendum (change order) as an indented child in chronological (sequence) order — terminal ones
// included — connected by the same connector the Quotes list uses. Each addendum hangs off its
// never-voided original contract via `originalContractId`.
function renderContractItems(
  items: ContractListData[],
  changeOrders: ChangeOrderDocument[] | undefined,
  quotes: Quote[] | undefined,
  preBillingCustomerDiscount: PreBillingCustomerDiscountStatus | undefined,
  canCreateChangeOrder: boolean | undefined,
  accountId: string,
  programType: ProgramType,
  isInstallApproved: boolean | undefined,
): React.ReactNode[] {
  const result: React.ReactNode[] = [];

  // Group addenda by their originalContractId, each group sorted by sequence ascending. Skip COs
  // with no originalContractId: String(undefined) === 'undefined' would false-match a contract
  // whose id is also unset.
  const addendaByContract = new Map<string, ChangeOrderDocument[]>();
  for (const co of changeOrders ?? []) {
    const key = co.originalContractId != null ? String(co.originalContractId) : undefined;
    if (!key) continue;
    const group = addendaByContract.get(key) ?? [];
    group.push(co);
    addendaByContract.set(key, group);
  }
  for (const group of addendaByContract.values()) {
    group.sort((a, b) => a.sequence - b.sequence);
  }

  items.forEach((item, index) => {
    const contractId = item.contract?.id != null ? item.contract.id : undefined;
    const addenda = contractId ? addendaByContract.get(contractId) : undefined;

    if (addenda?.length) {
      // The straight vertical line takes the most recent addendum's color, representing the chain's
      // current state; each arc takes its own addendum's color.
      const chainColor = getAddendumConnectorColor(addenda[addenda.length - 1].status);
      result.push(
        <Box key={contractId}>
          {/* Contract row padding-top is `md` (16px), so the parent badge bottom sits at ~38px. The
              line drops from there to the bottom of the contract row, handing off to the first
              addendum below. Scoping each segment to its own row (rather than one line spanning the
              whole group) lets the chain terminate at the last addendum's arc instead of dangling
              past the bottom of that row. */}
          <Box style={{ position: 'relative' }}>
            <ConnectorParentLine color={chainColor} top={38} />
            <ContractListItem
              item={item}
              preBillingCustomerDiscount={preBillingCustomerDiscount}
              canCreateChangeOrder={canCreateChangeOrder}
              programType={programType}
              isInstallApproved={isInstallApproved}
            />
          </Box>
          {addenda.map((co, addendumIndex) => {
            const subQuote = quotes?.find((q) => q.id === String(co.proposedQuoteId));
            const isLastAddendum = addendumIndex === addenda.length - 1;
            return (
              <Box key={`co-${String(co.id)}`} style={{ position: 'relative' }}>
                {/* Non-last addenda continue the straight line full-height to reach the next child;
                    the last addendum draws only its arc, so the line ends at the arc junction. */}
                {!isLastAddendum && <ConnectorParentLine color={chainColor} top={0} />}
                {/* The status badge's vertical center sits at ~32px from the child row's top. */}
                <ConnectorChildArc color={getAddendumConnectorColor(co.status)} height={32} />
                <ChangeOrderContractRow
                  changeOrder={co}
                  quote={subQuote}
                  indentLeft={CHILD_INDENT}
                  accountId={accountId}
                />
              </Box>
            );
          })}
        </Box>,
      );
    } else {
      result.push(
        <ContractListItem
          item={item}
          key={contractId ?? `contract-${index}`}
          preBillingCustomerDiscount={preBillingCustomerDiscount}
          canCreateChangeOrder={canCreateChangeOrder}
          programType={programType}
          isInstallApproved={isInstallApproved}
        />,
      );
    }
  });
  return result;
}

export function ContractsList({
  isLoading,
  items,
  programType,
  activeQuote,
  systemDesign,
  preBillingCustomerDiscount,
  canCreateChangeOrder,
  isInstallApproved,
  changeOrders,
  quotes,
  accountId,
}: {
  isLoading: boolean;
  items: ContractListData[];
  programType: ProgramType;
  activeQuote?: Quote;
  systemDesign?: SystemDesignReturnType<any>;
  preBillingCustomerDiscount?: PreBillingCustomerDiscountStatus;
  canCreateChangeOrder?: boolean;
  isInstallApproved?: boolean;
  changeOrders?: ChangeOrderDocument[];
  quotes?: Quote[];
  accountId: string;
}) {
  const noContractMessaging = () => {
    if (
      [ProgramType.hvac, ProgramType.waterHeater, ProgramType.generator, ProgramType.geothermal].includes(
        programType,
      )
    ) {
      if (!activeQuote && !systemDesign) {
        return 'Contract generation is pending Quote and System Design.';
      } else if (!activeQuote) {
        return 'Contract generation is pending Quote.';
      } else if (!systemDesign) {
        return 'Contract generation is pending System Design.';
      } else {
        return 'No contract exists for this account.';
      }
    } else {
      return 'No contract exists for this account.';
    }
  };
  return (
    <Box width="100" overflow="auto">
      {!isLoading && items.length > 0 ? (
        <Box>
          {renderContractItems(
            items,
            changeOrders,
            quotes,
            preBillingCustomerDiscount,
            canCreateChangeOrder,
            accountId,
            programType,
            isInstallApproved,
          )}
        </Box>
      ) : (
        <Box
          display="block"
          textAlign="center"
          padding="lg"
          childGap="xl"
          borderColor="separator"
          borderWidth="xs 0 0 0"
        >
          {isLoading ? (
            <Spinner size="lg" />
          ) : (
            <>
              <Box fontSize="sm" color="body-secondary">
                {noContractMessaging()}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
